import { CheckCircle, SkipForward } from "lucide-react";
import { Spinner, ProgressBar, GuessFeed } from "../../shared/ui";
import CircleTimer from "../../shared/CircleTimer";

// ── Crocodile Host Screen ─────────────────────────────────────────────────────
// Props:
//   explainer  : string | null
//   word       : string        — secret word (only shown to host)
//   cat        : string        — category
//   timeLeft   : number
//   maxTime    : number
//   guessedBy  : string[]      — names who guessed correctly
//   guesses    : { player, guess, correct }[]
//   roundEnd   : object | null — { word, skipped, time_expired, guessed_by }
//   players    : object[]
//   onSkip     : () => void
//   tv         : bool

export default function CrocodileHost({ explainer, word, cat, timeLeft, maxTime, guessedBy, guesses, roundEnd, players, onSkip, tv }) {
  const expData = players.find(p => p.name === explainer);
  const guessers = players.filter(p => p.name !== explainer).length;

  // Round end screen
  if (roundEnd) return (
    <div className="w-full text-center animate-slide-up">
      <div style={{ fontSize: tv ? "5rem" : "3.5rem", marginBottom: "0.65rem" }}>
        {roundEnd.skipped ? "⏭️" : roundEnd.time_expired ? "⏰" : "🎉"}
      </div>
      <h2 className="font-heading text-white mb-4" style={{ fontSize: tv ? "3.5rem" : "1.75rem" }}>
        {roundEnd.skipped ? "Пропущено!" : roundEnd.time_expired ? "Время вышло!" : "Все угадали!"}
      </h2>
      <div style={{
        display: "inline-block", background: "#1e0845", border: "3px solid #000",
        borderRadius: "1.5rem", boxShadow: "6px 6px 0 #000",
        padding: tv ? "1.75rem 2.75rem" : "1rem 1.75rem", marginBottom: "1.1rem",
      }}>
        <div className="font-body text-[#aaa] mb-1" style={{ fontSize: tv ? "1.1rem" : "0.8rem" }}>Загаданное слово:</div>
        <div className="font-display" style={{ fontSize: tv ? "4rem" : "2.5rem", color: "#FFE000", textShadow: "3px 3px 0 #000" }}>
          {roundEnd.word}
        </div>
      </div>
      <div className="font-heading" style={{ fontSize: tv ? "1.75rem" : "1.1rem", color: roundEnd.guessed_by.length ? "#3DFF3D" : "#aaa" }}>
        {roundEnd.guessed_by.length ? `Угадали: ${roundEnd.guessed_by.join(", ")}` : "Никто не угадал 😅"}
      </div>
      <div className="font-body text-[#444] mt-3" style={{ fontSize: tv ? "1rem" : "0.8rem" }}>Следующий раунд...</div>
    </div>
  );

  if (!explainer) return <Spinner color="#3DFF3D" tv={tv} />;

  return (
    <div className="w-full animate-slide-up">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 text-center">
          <div className="mb-4"><CircleTimer timeLeft={timeLeft} maxTime={maxTime} tv={tv} /></div>

          <div className="mb-4">
            <div className="font-body text-[#aaa] mb-1" style={{ fontSize: tv ? "1.25rem" : "0.8rem" }}>Объясняет:</div>
            <div style={{
              display: "inline-block", backgroundColor: expData?.color || "#3DFF3D",
              border: "3px solid #000", borderRadius: "1.25rem", boxShadow: "4px 4px 0 #000",
              padding: tv ? "0.65rem 2.25rem" : "0.45rem 1.35rem",
            }}>
              <span className="font-heading" style={{ fontSize: tv ? "2.75rem" : "1.6rem", color: "#000" }}>{explainer}</span>
            </div>
          </div>

          {cat && (
            <div style={{
              display: "inline-block", background: "#000", border: "2px solid #333",
              borderRadius: "9999px", padding: tv ? "0.45rem 1.5rem" : "0.3rem 1rem", marginBottom: "0.9rem",
            }}>
              <span className="font-body text-[#aaa]" style={{ fontSize: tv ? "1.1rem" : "0.8rem" }}>Категория: {cat}</span>
            </div>
          )}

          <div className="font-body text-[#aaa] mb-2" style={{ fontSize: tv ? "1.1rem" : "0.8rem" }}>
            Угадали: {guessedBy.length}/{guessers}
          </div>
          <ProgressBar value={guessedBy.length} max={guessers} color="#3DFF3D" tv={tv} />

          <div className="flex flex-wrap justify-center gap-2 mt-3 mb-4">
            {players.filter(p => p.name !== explainer).map(p => {
              const g = guessedBy.includes(p.name);
              return (
                <div key={p.name} className={`player-chip ${g ? "" : "opacity-30"}`}
                  style={{ backgroundColor: p.color, fontSize: tv ? "1.1rem" : "0.8rem", padding: tv ? "0.4rem 1.1rem" : "0.3rem 0.75rem" }}>
                  {g && <CheckCircle style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.2rem" }} />}
                  {p.name}
                </div>
              );
            })}
          </div>

          <button onClick={onSkip} data-testid="crocodile-host-skip-btn" className="btn-cartoon btn-dark"
            style={{ fontSize: tv ? "1.1rem" : "0.8rem", padding: tv ? "0.65rem 1.75rem" : "0.45rem 1.1rem" }}>
            <SkipForward style={{ width: "1rem", height: "1rem", display: "inline", marginRight: "0.3rem" }} />
            Пропустить
          </button>
        </div>

        <GuessFeed guesses={guesses} tv={tv} />
      </div>
    </div>
  );
}
